import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import {
  formatHsl,
  formatRgb,
  parseColor,
  rgbToHex,
  rgbToHsl,
} from "../lib/color";

const SAMPLE = "#6366f1";

function CopyRow({
  label,
  value,
  testid,
}: {
  label: string;
  value: string;
  testid: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card/40 p-4">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p data-testid={testid} className="mt-0.5 truncate font-mono text-sm">
          {value}
        </p>
      </div>
      <button
        type="button"
        onClick={copy}
        aria-label={`${m.cv_copy()} ${label}`}
        className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
      >
        {copied ? (
          <Check className="h-4 w-4 text-emerald-500" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

export function ColorConverter() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const parsed = useMemo(() => parseColor(input), [input]);

  const rgb = parsed.ok ? parsed.rgb : null;
  const hex = rgb ? rgbToHex(rgb.r, rgb.g, rgb.b) : null;
  const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : null;
  const pickerHex = hex ?? "#000000";

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={`${uid}-in`}>{m.cv_input_label()}</Label>
            <Input
              id={`${uid}-in`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="#6366f1"
              spellCheck={false}
              autoCapitalize="none"
              className="font-mono"
            />
          </div>
          <input
            type="color"
            aria-label={m.cv_picker_label()}
            value={pickerHex}
            onChange={(e) => setInput(e.target.value)}
            className="h-10 w-14 cursor-pointer rounded-md border border-border bg-transparent"
          />
        </div>
      </div>

      {rgb && hex && hsl ? (
        <div className="space-y-4">
          <div
            data-testid="cv-swatch"
            aria-hidden
            className="h-28 w-full rounded-2xl border border-border"
            style={{ backgroundColor: hex }}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <CopyRow label="HEX" value={hex} testid="cv-hex" />
            <CopyRow label="RGB" value={formatRgb(rgb)} testid="cv-rgb" />
            <CopyRow label="HSL" value={formatHsl(hsl)} testid="cv-hsl" />
          </div>
        </div>
      ) : (
        <p
          data-testid="cv-error"
          className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300"
        >
          {m.cv_invalid()}
        </p>
      )}
    </div>
  );
}
